import { useState } from 'react';
import { NavigationBar } from '../../components/ui/NavigationBar';
import { ChevronDown, ChevronUp, Copy, Check, Car, ClipboardList, StickyNote, FileUp, MapPin, Wrench, Bell, Info, ShieldCheck, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type Method = 'GET' | 'PUT' | 'PATCH' | 'DELETE';
type Category = 'GENERAL' | 'PROFILE' | 'CARS' | 'EXPENSE TYPES' | 'LOG ENTRIES' | 'RECURRING EXPENSES' | 'NOTES' | 'SPARE PARTS' | 'LOCATIONS' | 'REMINDERS' | 'FILES';

interface Endpoint {
  id: string;
  category: Category;
  method: Method;
  path: string;
  summary: string;
  description: string;
  authRequired: boolean;
  requestBody?: string;
  responseBody?: string;
  mandatoryFields?: string[];
  queryParams?: string[];
}

const ERROR_FORMAT = `{
  "error": "Error title",
  "code": "ERROR_CODE",
  "message": "Detailed explanation of what went wrong."
}`;

const ENDPOINTS: Endpoint[] = [
  // PROFILE
  {
    id: 'get-profile',
    category: 'PROFILE',
    method: 'GET',
    path: '/v1/profile',
    summary: 'Get user profile & limits',
    description: 'Retrieve current settings, plan details, and storage usage statistics.',
    authRequired: true,
    responseBody: `{
  "id": "user-uuid",
  "email": "user@example.com",
  "plan_type": "pro",
  "settings": {
    "language": "en",
    "theme": "dark",
    "distance_unit": "km",
    "volume_unit": "L",
    "currency": "EUR"
  },
  "usage": {
    "used_storage_bytes": 104857600,
    "storage_limit_bytes": 2147483648,
    "daily_uploads": 12,
    "daily_limit": 100
  }
}`
  },
  {
    id: 'patch-profile',
    category: 'PROFILE',
    method: 'PATCH',
    path: '/v1/profile',
    summary: 'Update settings',
    description: 'Modify user preferences like language or units.',
    authRequired: true,
    requestBody: `{ "language": "lv", "theme": "light", "distance_unit": "miles" }`,
    responseBody: `{ "status": "updated", "data": { ... } }`
  },

  // EXPENSE TYPES
  {
    id: 'get-expense-types',
    category: 'EXPENSE TYPES',
    method: 'GET',
    path: '/v1/expense-types',
    summary: 'List allowed categories',
    description: 'Returns the official list of expense keys. Mandatory for Log Entry creation.',
    authRequired: false,
    responseBody: `[ { "id": "fuel", "label": "Fuel" }, { "id": "service_maintenance", "label": "Service" } ]`
  },

  // CARS
  {
    id: 'get-cars',
    category: 'CARS',
    method: 'GET',
    path: '/v1/cars',
    summary: 'List garage',
    description: 'Retrieve all vehicles with pagination.',
    authRequired: true,
    queryParams: ['limit', 'last_updated'],
    responseBody: `[ { "id": "uuid", "name": "Grey Audi", "is_archived": false, "updated_at": "..." } ]`
  },
  {
    id: 'get-car-single',
    category: 'CARS',
    method: 'GET',
    path: '/v1/cars/{slug}',
    summary: 'Full car details',
    description: 'Technical data, scores, and sell status. Slug is immutable.',
    authRequired: true,
    responseBody: `{ "id": "uuid", "slug": "audi-a6", "is_archived": false, "sell_price": null, "updated_at": "..." }`
  },
  {
    id: 'get-car-stats',
    category: 'CARS',
    method: 'GET',
    path: '/v1/cars/{slug}/stats',
    summary: 'Financial analytics',
    description: 'Aggregated costs and mileage stats for the dashboard.',
    authRequired: true,
    responseBody: `{ "total_expenses": 1250.00, "cost_per_km": 0.15, "expense_by_type": { ... } }`
  },
  {
    id: 'put-car',
    category: 'CARS',
    method: 'PUT',
    path: '/v1/cars',
    summary: 'Add vehicle',
    description: 'Register a new car.',
    authRequired: true,
    mandatoryFields: ['name', 'make', 'model'],
    requestBody: `{ "name": "BMW", "make": "BMW", "model": "330", "year": 2020 }`,
    responseBody: `{ "id": "uuid", "slug": "bmw-330", "status": "created" }`
  },
  {
    id: 'patch-car',
    category: 'CARS',
    method: 'PATCH',
    path: '/v1/cars/{slug}',
    summary: 'Update / Archive',
    description: 'Archive by setting is_archived: true and providing sell_date/price.',
    authRequired: true,
    requestBody: `{ "is_archived": true, "sell_price": 15000, "sell_date": "2026-05-04" }`,
    responseBody: `{ "status": "updated" }`
  },

  // LOG ENTRIES
  {
    id: 'get-log-entries',
    category: 'LOG ENTRIES',
    method: 'GET',
    path: '/v1/cars/{slug}/log-entries',
    summary: 'History & Search',
    description: 'Fetch history. Use "q" for full-text search across titles/descriptions.',
    authRequired: true,
    queryParams: [
      'limit (integer, default: 50)',
      'last_updated (string, ISO8601)',
      'q (string, search query)',
      'type (string, filter by category)'
    ],
    responseBody: `[ { "id": "uuid", "title": "Service", "mileage": 15000, "updated_at": "..." } ]`
  },
  {
    id: 'get-log-entry-single',
    category: 'LOG ENTRIES',
    method: 'GET',
    path: '/v1/cars/{slug}/log-entries/{id}',
    summary: 'Deep entry view',
    description: 'Includes nested expenses, parts and file URLs.',
    authRequired: true,
    responseBody: `{ "id": "uuid", "expenses": [...], "parts": [...], "files": [...], "updated_at": "..." }`
  },
  {
    id: 'put-log-entry',
    category: 'LOG ENTRIES',
    method: 'PUT',
    path: '/v1/cars/{slug}/log-entry',
    summary: 'Atomic creation',
    description: 'Create entry with nested expenses/parts/files in one transaction.',
    authRequired: true,
    mandatoryFields: ['date', 'mileage'],
    requestBody: `{ "date": "...", "mileage": 120, "expenses": [...], "parts": [...], "file_ids": [...] }`,
    responseBody: `{ "id": "uuid", "status": "created" }`
  },

  // RECURRING EXPENSES
  {
    id: 'get-repeats',
    category: 'RECURRING EXPENSES',
    method: 'GET',
    path: '/v1/cars/{slug}/recurring',
    summary: 'List auto-generators',
    description: 'List configurations that automatically generate log entries based on templates.',
    authRequired: true,
    responseBody: `[ 
  { 
    "id": "uuid", 
    "interval_type": "month", 
    "interval_value": 1, 
    "start_date": "...",
    "next_generation_date": "...",
    "copy_title": true,
    "copy_location": false,
    "copy_description": true,
    "updated_at": "..." 
  } 
]`
  },
  {
    id: 'put-repeat',
    category: 'RECURRING EXPENSES',
    method: 'PUT',
    path: '/v1/cars/{slug}/recurring',
    summary: 'Setup recurrence',
    description: 'Link a template log entry to generate future records automatically. Supports copying location/title/description.',
    authRequired: true,
    mandatoryFields: ['template_log_entry_id', 'interval_value', 'start_date'],
    requestBody: `{ 
  "template_log_entry_id": "uuid", 
  "interval_type": "month", 
  "interval_value": 1, 
  "start_date": "2026-05-01",
  "end_date": null,
  "copy_title": true,
  "copy_location": true,
  "copy_description": false
}`,
    responseBody: `{ "id": "uuid", "status": "created" }`
  },
  {
    id: 'delete-repeat',
    category: 'RECURRING EXPENSES',
    method: 'DELETE',
    path: '/v1/cars/{slug}/recurring/{id}',
    summary: 'Stop recurrence',
    description: 'Disable automatic generation for this configuration.',
    authRequired: true,
    responseBody: `{ "status": "deleted" }`
  },

  // NOTES
  {
    id: 'get-notes',
    category: 'NOTES',
    method: 'GET',
    path: '/v1/cars/{slug}/notes',
    summary: 'List notes',
    description: 'Supports pagination and is_temporary filtering.',
    authRequired: true,
    queryParams: ['limit', 'last_updated'],
    responseBody: `[ { "id": "uuid", "content": "...", "is_temporary": false, "updated_at": "..." } ]`
  },
  {
    id: 'put-note',
    category: 'NOTES',
    method: 'PUT',
    path: '/v1/cars/{slug}/notes',
    summary: 'Create note',
    description: 'Supports HTML. Use is_temporary for drafts.',
    authRequired: true,
    mandatoryFields: ['content'],
    requestBody: `{ "content": "...", "is_temporary": true }`,
    responseBody: `{ "id": "uuid", "status": "created" }`
  },

  // SPARE PARTS
  {
    id: 'get-parts',
    category: 'SPARE PARTS',
    method: 'GET',
    path: '/v1/cars/{slug}/parts',
    summary: 'Inventory list',
    description: 'Catalog of parts with usage statistics and is_temporary status.',
    authRequired: true,
    responseBody: `[ { "id": "uuid", "name": "Filter", "total_uses": 5, "is_temporary": false, "updated_at": "..." } ]`
  },
  {
    id: 'put-part',
    category: 'SPARE PARTS',
    method: 'PUT',
    path: '/v1/cars/{slug}/parts',
    summary: 'Add to catalog',
    description: 'Register a new spare part. Use is_temporary: true for AI-suggested parts.',
    authRequired: true,
    mandatoryFields: ['name'],
    requestBody: `{ "name": "Brake Pads", "part_code": "P85075", "manufacturer": "Brembo", "is_temporary": false }`,
    responseBody: `{ "id": "uuid", "status": "created" }`
  },
  {
    id: 'patch-part',
    category: 'SPARE PARTS',
    method: 'PATCH',
    path: '/v1/cars/{slug}/parts/{id}',
    summary: 'Update part',
    description: 'Modify part data or confirm a temporary part.',
    authRequired: true,
    requestBody: `{ "is_temporary": false, "price": 65.00 }`,
    responseBody: `{ "status": "updated" }`
  },
  {
    id: 'delete-part',
    category: 'SPARE PARTS',
    method: 'DELETE',
    path: '/v1/cars/{slug}/parts/{id}',
    summary: 'Delete part',
    description: 'Remove a part from inventory.',
    authRequired: true,
    responseBody: `{ "status": "deleted" }`
  },

  // LOCATIONS
  {
    id: 'get-locations',
    category: 'LOCATIONS',
    method: 'GET',
    path: '/v1/locations',
    summary: 'List places',
    description: 'Saved service centers, gas stations, etc. Includes is_temporary status.',
    authRequired: true,
    responseBody: `[ { "id": "uuid", "name": "My Shop", "address": "...", "lat": 56.9, "lng": 24.1, "is_temporary": false } ]`
  },
  {
    id: 'put-location',
    category: 'LOCATIONS',
    method: 'PUT',
    path: '/v1/locations',
    summary: 'Create location',
    description: 'Register a new spot. Use is_temporary: true if suggest by AI.',
    authRequired: true,
    mandatoryFields: ['name'],
    requestBody: `{ "name": "My Shop", "address": "...", "lat": 56.9, "lng": 24.1, "is_temporary": false }`,
    responseBody: `{ "id": "uuid", "status": "created" }`
  },
  {
    id: 'patch-location',
    category: 'LOCATIONS',
    method: 'PATCH',
    path: '/v1/locations/{id}',
    summary: 'Update location',
    description: 'Modify location details or confirm status.',
    authRequired: true,
    requestBody: `{ "is_temporary": false, "name": "Verified Shop" }`,
    responseBody: `{ "status": "updated" }`
  },

  // REMINDERS
  {
    id: 'get-reminders',
    category: 'REMINDERS',
    method: 'GET',
    path: '/v1/cars/{slug}/reminders',
    summary: 'Active alerts',
    description: 'List future maintenance alerts.',
    authRequired: true,
    responseBody: `[ { "id": "uuid", "name": "Oil Change", "remind_date": "...", "is_seen": false } ]`
  },
  {
    id: 'patch-reminder-seen',
    category: 'REMINDERS',
    method: 'PATCH',
    path: '/v1/cars/{slug}/reminders/{id}/seen',
    summary: 'Acknowledge',
    description: 'Mark as read to stop notifications.',
    authRequired: true,
    responseBody: `{ "status": "updated", "is_seen": true }`
  },

  // FILES
  {
    id: 'put-upload',
    category: 'FILES',
    method: 'PUT',
    path: '/v1/upload',
    summary: 'OCR Upload',
    description: 'Upload for data extraction. HEIC is NOT supported.',
    authRequired: true,
    requestBody: `FormData { file: Binary, need_ai_check: Boolean }`,
    responseBody: `{ "file_id": "uuid", "ocr_preview": { ... } }`
  }
];

export function ApiDocsView({ onBack }: { onBack: () => void }) {
  const [expandedId, setExpandedId] = useState<string | null>('get-cars');

  const categories: Category[] = ['GENERAL', 'PROFILE', 'CARS', 'EXPENSE TYPES', 'LOG ENTRIES', 'RECURRING EXPENSES', 'NOTES', 'SPARE PARTS', 'LOCATIONS', 'REMINDERS', 'FILES'];
  const catIcons = {
    GENERAL: <Info size={18} />,
    PROFILE: <ShieldCheck size={18} />,
    'EXPENSE TYPES': <ClipboardList size={18} />,
    CARS: <Car size={18} />,
    'LOG ENTRIES': <ClipboardList size={18} />,
    'RECURRING EXPENSES': <ClipboardList size={18} />,
    NOTES: <StickyNote size={18} />,
    'SPARE PARTS': <Wrench size={18} />,
    LOCATIONS: <MapPin size={18} />,
    REMINDERS: <Bell size={18} />,
    FILES: <FileUp size={18} />
  };

  return (
    <div className="fixed inset-0 flex flex-col h-full bg-[#fafafa] dark:bg-[#0f172a] overflow-hidden z-50 font-sans">
      <div className="flex-shrink-0">
        <NavigationBar
          title="API Reference"
          onBack={onBack}
          fixed={false}
        />
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar pb-32">
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-12">
          
          <header className="space-y-4">
            <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
              Developer <span className="text-blue-600 dark:text-blue-400">Portal</span>
            </h1>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl text-base leading-relaxed">
              Exhaustive technical documentation for the MyCarCosts Public API. Designed for high-reliability integrations and AI-driven automation.
            </p>
          </header>

          {/* GENERAL INFO SECTION */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-2xl space-y-4">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-bold">
                <ShieldCheck size={20} />
                <h3>Authentication</h3>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-normal">
                All requests must include the <code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">X-API-Token</code> header. 
                Generate tokens in your <span className="underline italic">Settings &gt; API</span>.
              </p>
              <div className="p-3 bg-slate-900 rounded-xl text-[10px] font-mono text-slate-300">
                curl -H "X-API-Token: YOUR_TOKEN" https://api.mycarcosts.com/v1/cars
              </div>
            </div>

            <div className="p-6 bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-800/30 rounded-2xl space-y-4">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-bold">
                <AlertCircle size={20} />
                <h3>Standard Errors</h3>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-normal">
                If a request fails, you will receive a non-2xx status code and a standard error object.
              </p>
              <CodeBlock code={ERROR_FORMAT} color="slate" />
            </div>
          </section>

          {categories.filter(c => c !== 'GENERAL').map(cat => (
            <section key={cat} className="space-y-4">
              <div className="flex items-center gap-2 px-1 text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-3">
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-600 dark:text-slate-400">
                  {catIcons[cat]}
                </div>
                <h2 className="text-base font-black tracking-widest uppercase">{cat}</h2>
              </div>
              <div className="space-y-4">
                {ENDPOINTS.filter(ep => ep.category === cat).map(ep => (
                  <EndpointCard 
                    key={ep.id} 
                    endpoint={ep} 
                    isExpanded={expandedId === ep.id}
                    onToggle={() => setExpandedId(expandedId === ep.id ? null : ep.id)}
                  />
                ))}
              </div>
            </section>
          ))}

          {/* DATA TYPES SECTION */}
          <section className="p-8 bg-slate-100 dark:bg-slate-800/50 rounded-3xl space-y-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Technical Constraints</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-sm">
              <div className="space-y-2">
                <h4 className="font-bold text-blue-500">Numerical Data</h4>
                <ul className="space-y-1 text-slate-600 dark:text-slate-400 text-xs">
                  <li><b>Mileage:</b> Integer (up to 9,999,999)</li>
                  <li><b>Amounts:</b> Float / Numeric (2 decimal places)</li>
                  <li><b>Coordinates:</b> Float (Double precision)</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-emerald-500">File Constraints</h4>
                <ul className="space-y-1 text-slate-600 dark:text-slate-400 text-xs">
                  <li><b>Formats:</b> JPG, PNG, PDF</li>
                  <li><b>Limits:</b> 25MB (Free) / 50MB (Pro) per file</li>
                  <li><b>HEIC:</b> Not supported for direct API uploads. Convert to JPG/PNG before sending.</li>
                </ul>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

function EndpointCard({ endpoint, isExpanded, onToggle }: { endpoint: Endpoint, isExpanded: boolean, onToggle: () => void }) {
  const methodColors = {
    GET: 'bg-blue-500 border-blue-600',
    PUT: 'bg-emerald-500 border-emerald-600',
    PATCH: 'bg-indigo-500 border-indigo-600',
    DELETE: 'bg-red-500 border-red-600'
  };

  const methodBgLight = {
    GET: 'bg-blue-50/30 dark:bg-blue-950/5',
    PUT: 'bg-emerald-50/30 dark:bg-emerald-950/5',
    PATCH: 'bg-indigo-50/30 dark:bg-indigo-950/5',
    DELETE: 'bg-red-50/30 dark:bg-red-950/5'
  };

  return (
    <div className={`border rounded-2xl overflow-hidden transition-all duration-300 ${isExpanded ? 'ring-2 ring-slate-200 dark:ring-slate-800 shadow-xl scale-[1.01]' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'} bg-white dark:bg-slate-900`}>
      <button 
        onClick={onToggle}
        className={`w-full flex items-center gap-4 p-4 text-left transition-colors ${isExpanded ? 'bg-slate-50/50 dark:bg-slate-800/30' : ''}`}
      >
        <span className={`w-24 text-center text-[10px] font-black py-1.5 rounded-lg text-white shadow-md ${methodColors[endpoint.method]}`}>
          {endpoint.method}
        </span>
        <span className="font-mono text-sm font-bold text-slate-700 dark:text-slate-300 flex-1 truncate">
          {endpoint.path}
        </span>
        {isExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className={`p-8 border-t border-slate-100 dark:border-slate-800 space-y-8 ${methodBgLight[endpoint.method]}`}>
              <div className="space-y-2">
                <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Context & Behavior</h4>
                <p className="text-sm font-bold text-slate-800 dark:text-white leading-tight">{endpoint.summary}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl">
                  {endpoint.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {endpoint.queryParams && (
                  <div className="space-y-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-blue-500">Query Parameters</h4>
                    <div className="space-y-2">
                      {endpoint.queryParams.map(q => (
                        <div key={q} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400 font-mono">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />
                          {q}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {endpoint.mandatoryFields && (
                  <div className="space-y-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-red-500">Required Body Fields</h4>
                    <div className="flex flex-wrap gap-2">
                      {endpoint.mandatoryFields.map(f => (
                        <span key={f} className="px-2.5 py-1 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-[10px] font-black rounded-lg border border-red-100 dark:border-red-900/30 uppercase">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {endpoint.requestBody && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Request Body</h4>
                    <span className="text-[10px] font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-slate-500">application/json</span>
                  </div>
                  <CodeBlock code={endpoint.requestBody} />
                </div>
              )}

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Response Payload</h4>
                  <span className="text-[10px] font-mono text-emerald-500 font-bold bg-emerald-50 dark:bg-emerald-950/20 px-2 py-1 rounded border border-emerald-100 dark:border-emerald-900/30">200 OK / 201 Created</span>
                </div>
                <CodeBlock code={endpoint.responseBody || '{}'} color="emerald" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CodeBlock({ code, color = 'slate' }: { code: string, color?: 'slate' | 'emerald' }) {
  const [copied, setCopied] = useState(false);

  const colors = {
    slate: 'bg-slate-900 border-slate-800 text-slate-300',
    emerald: 'bg-[#061613] border-emerald-900/30 text-emerald-400/90'
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`relative group border rounded-2xl overflow-hidden ${colors[color]}`}>
      <pre className="p-6 font-mono text-[11px] leading-relaxed overflow-x-auto no-scrollbar">
        {code}
      </pre>
      <button 
        onClick={handleCopy}
        className="absolute top-4 right-4 p-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/40 hover:text-white/80 transition-all opacity-0 group-hover:opacity-100 backdrop-blur-sm"
      >
        {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
      </button>
    </div>
  );
}
